import app from 'flarum/forum/app';

/**
 * v0.10.13 — escapeCssUrl() — bg_image → inline style 的**唯一**转义入口
 *
 * 为什么抽成共享 helper (跟 v0.10.12 抽 BgImageValidator 同一个理由):
 *   'url.replace(/"/g, \'\\"\')' 这行逐字节相同地散在 4 个文件里
 *   (CardBackground / CardTemplatesTab / CardTemplateEditorModal ×2)。
 *   漏一处就是一个注入点, 而且 review 时极难看出来 —— 4 处长得一模一样。
 *   抽成单一导出后, 4 处不可能再分叉。
 *
 * 上一版加固只转义了双引号, **漏了反斜杠本体**, 也没剥控制字符。
 * 在 CSS 字符串语境里 '\' 是转义引导符: 攻击者只要在 URL 里放一个 '\',
 * 就能让紧随其后的字符被 CSS 解析器当成「已转义」而跳过引号闭合检查,
 * 从而提前闭合 url("...") 并在其后注入任意 CSS 声明。
 *
 * 具体可利用性说明 —— **v0.15.2 订正, 原注释方向反了**:
 *   v0.10.12 brief / v0.10.13 注释都断言「v0.12 后端 BgImageValidator 已经把值守死在
 *   「^https?:// 或 /api/ziiven/scheduler/card-bg/ 前缀 + ^[A-Za-z0-9_-]{1,64}$ 后缀」,
 *   存不进 '\ " ;' ⇒ 本层只是纵深防御」。**这句话是错的。**
 *   - 错在哪: 那个「白名单」只对**分支 ② (内置图相对路径)** 成立。**分支 ① (http(s) 绝对 URL)
 *     当时是 `if (preg_match('#^https?://#i', $s)) { return $s; }` —— 零字符过滤**,
 *     对反斜杠 / 引号 / 分号 / 换行 / 空格全部放行。
 *     v0.15.2 实测 (直接 require 真实源码调 validate()):
 *       'https://x.com/a.jpg";background-color:red;--z:"'   → 通过, 原样返回
 *       'https://x.com/a.jpg;position:fixed;width:100vw'    → 通过, 原样返回
 *       "https://x.com/a.jpg\nbackground-color:red"       → 通过, 裸换行原样返回
 *       "https://evil.com/x.png\\',url(https://evil.com)"   → 通过, 原样返回
 *     `javascript:` / `data:` 被拒纯粹是因为那个正则**不匹配**, 不是因为做了字符过滤。
 *   - 正确结论: **escapeCssUrl() 是 load-bearing 的真修复, 不是冗余**。在后端补上防线之前,
 *     这一层是唯一拦得住 CSS 注入的东西。
 *   - 现状: v0.15.2 已给分支 ① 补上字符拒绝清单 + parse_url 结构校验
 *     (见 src/Helpers/BgImageValidator.php::assertSafeAbsoluteUrl)。**但不改变「前端转义必须
 *     自己站得住」这个结论** —— 后端白名单将来可能被放宽/改写, 两层要各自能独立拦住。
 */

/**
 * CSS url("...") 字符串转义。
 *
 * ⚠️ 转义顺序是硬要求, 反过来做等于没修:
 *   必须**先转义反斜杠 '\' → '\\', 再转义双引号 '"' → '\"'**。
 *   反过来 (先引号后反斜杠) 会把攻击者原本的 '\"' 变成 '\\"',
 *   '\\' 被解析成字面反斜杠, 后面的 '"' 反而变成**裸引号**, 重新打开注入面。
 *   典型 payload: https://x.com/a\");background:red;--a:url("
 *
 * 控制字符 (\r \n \t 等 ASCII 0x00–0x1F + DEL 0x7F) 直接剥除:
 *   它们在 URL 里没有合法用途 (该用 %0A 之类百分号编码),
 *   而 CSS 规范里字符串内的裸换行是**解析错误**并会终结该字符串 —— 只扩大注入面。
 *   注意**不剥普通空格 (0x20)**: 0x20 不在控制字符范围内,
 *   且字面空格虽不标准但可能出现在合法 URL 里, 不属于本次要管的。
 *
 * @param {*} raw 原始入参 (字符串 / null / undefined / 其他)
 * @returns {string} 转义后的 URL; null / undefined → '' (安全返回, 不抛异常)
 */
export function escapeCssUrl(raw) {
  if (raw === null || raw === undefined) return '';

  // 第 1 步: 剥 ASCII 控制字符 + DEL (在转义之前剥, 因为剥除不会引入 '\' 或 '"')
  let out = String(raw).replace(/[\x00-\x1F\x7F]/g, '');

  // 第 2 步: 先转义反斜杠本体 —— 顺序不能动, 见上方注释
  out = out.replace(/\\/g, '\\\\');

  // 第 3 步: 再转义双引号
  out = out.replace(/"/g, '\\"');
  return out;
}

/**
 * v0.17.4 — 内置背景图资源版本戳 (换图后破 7 天 immutable 缓存)
 *
 * 背景: CardBgImageController 的响应头是
 *   `Cache-Control: public, max-age=604800, immutable` (7 天不可变)。
 * v0.17.4 把 8 张内置图整个换掉, 而**数据库存量 bg_image 是不带版本参数的裸 URL**
 * (/api/ziiven/scheduler/card-bg/n1 ~ n8) —— 只给 card-bg/list 接口返的 url 加戳是
 * 不够的, 因为渲染卡片读的是 DB 里的值, 不是 list 接口的值。
 *
 * 所以版本戳统一加在**渲染出口** cardBackgroundStyle() 上:
 *   - 内置图 URL (路径含 /api/ziiven/scheduler/card-bg/) → 末尾追加 ?v=2
 *   - 自定义 COS 图 → **原样不动** (预签名 URL 动一个字符就 403)
 *
 * 换图时把下面两个常量的数字 +1, 并同步改 CardBgListController::ASSET_VERSION。
 */
export const BUILTIN_BG_VERSION = 2;

/** 内置图路径段 (跟 BgImageValidator::BUILTIN_PATH_PREFIX 一致) */
export const BUILTIN_BG_PATH = '/api/ziiven/scheduler/card-bg/';

/** 裸 id 形态 (DB 里理论上不存, 但历史数据/手工录入可能有) —— n1~n9 + n10, 别写成 [1-8] */
const BARE_ID_RE = /^n(?:[1-9]|10)$/;

/**
 * 把 URL 拆成 { path, query, hash } (query 不含前导 '?')。
 * 只做切分, 不做 URL 解码 —— 这里只是找位置, 避免把 %26 之类误当分隔符。
 */
function splitUrl(raw) {
  const url = String(raw == null ? '' : raw);
  const hashIdx = url.indexOf('#');
  const hash = hashIdx === -1 ? '' : url.slice(hashIdx);
  const noHash = hashIdx === -1 ? url : url.slice(0, hashIdx);
  const qIdx = noHash.indexOf('?');
  return {
    path: qIdx === -1 ? noHash : noHash.slice(0, qIdx),
    query: qIdx === -1 ? '' : noHash.slice(qIdx + 1),
    hash
  };
}

/**
 * v0.17.4 — 判断是不是内置图 URL。
 *
 * 用 `indexOf(内置路径段) !== -1` 而不是 `startsWith`, 是为了同时认出
 *   1) 相对路径 /api/ziiven/scheduler/card-bg/n1 (DB 存量形态)
 *   2) 绝对 URL   http://host/api/ziiven/scheduler/card-bg/n1 (手工填的)
 * 同时不会误伤 COS 图 —— COS 的 key 是 `scheduler_card_bg/xxx.jpg` (下划线),
 * 且要命中必须整段 `/api/ziiven/scheduler/card-bg/` 一字不差。
 */
export function isBuiltinBgUrl(raw) {
  return splitUrl(raw).path.indexOf(BUILTIN_BG_PATH) !== -1;
}

/**
 * v0.17.4 — 内置图 URL 加/刷新版本戳 (幂等: 已带 v= 时替换而不是叠加)。
 *
 * ⚠️ 非内置图 (COS 自定义图) 一律原样返回, 连字符串都不碰。
 *
 * @param {*} raw 原始 URL
 * @returns {string} 内置图 → 带 `?v=<BUILTIN_BG_VERSION>` 的 URL; 其它 → 原值
 */
export function withBuiltinVersion(raw) {
  const url = String(raw == null ? '' : raw);
  const {
    path,
    query,
    hash
  } = splitUrl(url);
  if (path.indexOf(BUILTIN_BG_PATH) === -1) return url;
  // 剥掉已有的 v= 参数, 保证同一个 URL 反复调用不会变成 ?v=2?v=2
  const kept = query ? query.split('&').filter(p => p && !/^v=/i.test(p)) : [];
  kept.push('v=' + BUILTIN_BG_VERSION);
  return path + '?' + kept.join('&') + hash;
}

/**
 * v0.17.4 — 存库用规范值: 内置图剥掉版本戳。
 *
 * 为什么必须有这个函数 (别绕过它直接存 list 接口的 url):
 *   BgImageValidator 分支② 校验的是 `/api/ziiven/scheduler/card-bg/` 之后的**整个后缀**
 *   必须匹配 `^[A-Za-z0-9_-]{1,64}$`。`n1?v=2` 里的 `?` 和 `=` 都不在字符类里
 *   ⇒ 存带戳的值会被后端 422 ValidationException 拒掉。
 *   所以版本戳只活在「渲染」这一侧, 库里永远是不带戳的规范值。
 *
 * COS 图不受影响 (预签名 URL 的 query 必须原样保留)。
 *
 * @param {*} raw 原始 URL
 * @returns {string} 内置图 → 不带 query 的规范值; 其它 → 原值
 */
export function builtinBgStoreValue(raw) {
  const url = String(raw == null ? '' : raw);
  if (!isBuiltinBgUrl(url)) return url;
  return splitUrl(url).path;
}

/**
 * v0.8.1 — 卡种背景图 URL 解析 + inline style (辉哥 10/6 拍板银行卡背景)
 *
 * bg_image 字段存储格式 (来自 src/Model/CardTemplate.php fillable):
 *   1) 内置图 — DB 已存为完整相对路径: '/api/ziiven/scheduler/card-bg/n1' (无 .jpg 扩展名, 绕 MAMP nginx 拦截)
 *      也接受单纯 id 'n1'~'n10' (防御性, 自动补全为相对路径)
 *   2) 自定义图 — 完整 https URL (COS), 直接用
 *
 * 返回:
 *   - '' (空字符串) — bg_image 缺失/无效, LESS background-color #1b1f26 兜底
 *   - 'background-image: url("..."); background-size: cover; background-position: center;'
 *
 * v0.17.4: 内置图在这里统一追加 `?v=2` 破缓存; COS 图不加。
 *
 * 复用自 CardTemplatesTab.js:174 cardBackgroundStyle() — 学员列表 (桌面+移动) 共享
 */
export function cardBackgroundStyle(bgImage) {
  if (!bgImage) return '';
  let url = String(bgImage).trim();
  if (!url) return '';

  // 防御性: 单纯 'n1'~'n10' (不带 path) → 拼成内置图相对路径
  if (BARE_ID_RE.test(url)) {
    url = BUILTIN_BG_PATH + url;
  }

  // v0.17.4 — 内置图统一加版本戳 (COS 图原样返回)
  url = withBuiltinVersion(url);

  // v0.10.13 — 转义统一走共享 escapeCssUrl (先 '\' 后 '"' + 剥控制字符)
  const escaped = escapeCssUrl(url);
  return 'background-image: url("' + escaped + '"); background-size: cover; background-position: center;';
}
export default cardBackgroundStyle;
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/CardBackground', cardBackgroundStyle);